import { G, S } from '@mobily/ts-belt';
import { Box } from '@stitch-fix/mode-react';
import ProductCard, { ProductCardProps } from '@stitch-fix/mode-react/ProductCard';
import { InView } from 'react-intersection-observer';
import { FeedbackThumbs, SaveItemButton } from '@stitch-fix/product-card';
import { formatMoney } from '@stitch-fix/format';
import { SeeItOnMe } from '@stitch-fix/vision-shared';
import { Money, InventoryAvailability } from '../../types/definitions/vendor/graphql-schema.d';
import { ShopProductVariantFragment } from '../../utils/hooks/getSearchResults';
import { trackEvent, SearchResultsProductView, StrikethroughProductView, SEARCH_RESULTS_EVENT_CATEGORY } from '../../utils/events';
import { useResultsMachine } from '../../utils/machines/results/context-provider';
import styles from './styles.module.scss';
interface ResultProductCardProps {
  brand: string;
  isVisionEligible: boolean;
  name: string;
  shopProductVariant: ShopProductVariantFragment | null | undefined;
  index: number;
  showFeedbackThumbs?: boolean;
  visionSeeItOnMe1ItemEnabled?: boolean;
  visionSeeItOnMeZoomEnabled?: boolean;
}
const trackProductCardView = (skuId: string, queryId: string) => {
  const event: SearchResultsProductView = {
    action_name: 'impression_view',
    action_type: 'view',
    canonical_name: SEARCH_RESULTS_EVENT_CATEGORY,
    event_name: SEARCH_RESULTS_EVENT_CATEGORY,
    additional_context: {
      direct_buy: {
        query_id: queryId,
        sku_id: skuId,
        source: SEARCH_RESULTS_EVENT_CATEGORY
      }
    }
  };
  trackEvent(event);
};
const trackStrikethroughPrice = (shopProductVariant: ShopProductVariantFragment) => {
  const price = shopProductVariant.price?.value;
  const fullPrice = shopProductVariant.priceMarkdown?.fullPrice?.value;
  const skuId = shopProductVariant.sku;
  if (G.isNumber(price) && G.isNumber(fullPrice)) {
    const event: StrikethroughProductView = {
      action_name: 'strikethrough_price_view',
      action_type: 'view',
      canonical_name: SEARCH_RESULTS_EVENT_CATEGORY,
      event_name: 'strikethrough.search_results',
      additional_context: {
        direct_buy: {
          sku_id: skuId,
          price,
          full_price: fullPrice
        }
      }
    };
    trackEvent(event);
  }
};

/**
 * Tracks product events when a Product Card comes into view (`inView` is true).
 */
const onProductCardView = (inView: boolean, shopProductVariant: ShopProductVariantFragment, queryId: string) => {
  if (inView) {
    trackProductCardView(shopProductVariant.sku, queryId);
    trackStrikethroughPrice(shopProductVariant);
  }
};

/**
 * Formats the Product Card's PDP URL.
 * Sets the `source` and `query_id` query params for tracking events in the PDP.
 */
const getPDPUrl = (variantUrl: string, queryId: string) => {
  let pdpQueryParams = 'source=search_results';
  if (S.isNotEmpty(queryId)) {
    pdpQueryParams = `${pdpQueryParams}&query_id=${queryId}`;
  }
  return `${variantUrl}&${pdpQueryParams}`;
};

/**
 * Intakes availability sku state and returns an object
 * that constructs the Sticker Label
 */
const getStickerLabel = (availability: InventoryAvailability | null | undefined, discountLabel: string | null | undefined): ProductCardProps['stickerLabel'] => {
  if (availability === 'OUT_OF_STOCK') {
    return {
      text: 'Out of stock'
    };
  }
  if (G.isString(discountLabel)) {
    return {
      text: discountLabel,
      variant: 'citrus'
    };
  }
  return undefined;
};
/**
 * Takes in a price or Money object and formats it to a string,
 * ie '$0.00'
 */
const formatPrice = (price?: Money) => {
  if (!G.isObject(price)) return undefined;
  return formatMoney(price.value, {
    inCents: true
  });
};

/**
 * Intakes a sku object and returns props for price and fullPrice
 */
const getProductCardPrice = (shopProductVariant: ShopProductVariantFragment) => {
  const formattedPrice = formatPrice(shopProductVariant.price);
  const formattedFullPrice = formatPrice(shopProductVariant?.priceMarkdown?.fullPrice);
  if (formattedPrice) {
    return {
      price: formattedPrice,
      fullPrice: formattedFullPrice
    };
  }
  return {};
};

/**
 * this component handles rendering the product card and firing the
 * in view event
 */
const ResultProductCard = ({
  brand,
  isVisionEligible,
  name,
  shopProductVariant,
  index,
  showFeedbackThumbs = false,
  visionSeeItOnMe1ItemEnabled = false,
  visionSeeItOnMeZoomEnabled = false
}: ResultProductCardProps) => {
  const {
    state
  } = useResultsMachine();
  const queryId = state.matches('success') ? state.context.data.queryId : '';
  if (!shopProductVariant) {
    return null;
  }
  return <InView className={styles['product-card']} data-testid="product-card" onChange={inView => onProductCardView(inView, shopProductVariant, queryId)} triggerOnce>
      <Box mb={0.5}>
        <ProductCard {...getProductCardPrice(shopProductVariant)} brand={brand} name={name} heroSecondaryAction={showFeedbackThumbs ? <FeedbackThumbs skuId={shopProductVariant.sku} itemIndex={index} source="PRODUCT_SEARCH_RESULTS" page="search-results" sourceId={null} /> : null} heroAction={<SaveItemButton skuId={shopProductVariant.sku} isSaved={shopProductVariant.saved} productEventsContext={{
        source: 'search-results'
      }} />} heroMedia={shopProductVariant.media[0].mediaPath ? {
        alt: shopProductVariant.media[0].altText,
        baseUrl: shopProductVariant.media[0].baseUrl || '',
        mediaPath: shopProductVariant.media[0].mediaPath || '',
        sizes: {
          sm: 320
        }
      } : undefined} heroMediaUnavailableText="IMAGE UNAVAILABLE" href={getPDPUrl(shopProductVariant.url, queryId)} sku={shopProductVariant.sku} stickerLabel={getStickerLabel(shopProductVariant.inventory?.availability, shopProductVariant.priceMarkdown?.discountLabel)} promoDiscountLabel={shopProductVariant.promoDiscountOffer?.displayLabel?.formatted ?? undefined} heroBottomLeftContent={visionSeeItOnMe1ItemEnabled && isVisionEligible && shopProductVariant.sku ? <SeeItOnMe key={shopProductVariant.sku} skuIds={[shopProductVariant.sku]} eventData={{
        sourceApp: 'categories-next-ui'
      }} variant="icon" enableZoom={visionSeeItOnMeZoomEnabled} waysToWearOutfits={{
        includeSingleSkuLook: false,
        outfitsLimit: 2
      }} /> : undefined} />
      </Box>
    </InView>;
};
export default ResultProductCard;